/*
 * File: ReportModal.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:30 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 30, 2025 at 2:47 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import validator from 'validator';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation, Trans } from 'react-i18next';

// Utilities
import { toastMessage, toastSuccess } from '../../utilities/toaster';

// Services
import { reportContent } from '../../services/report';

// Components
import { Modal } from '../Modal';
import { Typography } from '../Typography';
import { Button } from '../Button';
import { TextArea } from '../TextArea';
import { RadioGroup } from '../RadioGroup';

// Constants
import { AVAILABLE_I18N_NAMESPACES, REPORTING_REASONS, SOURCES } from '../../../constants/base.constants';

// Styles
import * as S from './ReportModal.styles';


/**
 * Component
 */

export const ReportModal = ({
	isOpen, handleClose, hideContent, source, sourceId
}) => {

	// Create state
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [inputValues, setInputValues] = useState({});
	const [reportReason, setReportReason] = useState(null);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Get actions from hooks
	const { executeRecaptcha } = useGoogleReCaptcha();

	// Handle submission
	const submitContentReport = async (e) => {

		// Prevent default
		e.preventDefault();

		// Get parameters
		const { additionalDetails } = inputValues;

		// Validate parameters
		if (!reportReason) {

			// Show error
			toastMessage(uiMode, partner, t('reportModal.missingReasonError'));

			// Return
			return;
		}

		// Update submission state
		setIsSubmitting(true);

		// Create parameters
		const params = {
			source,
			sourceId,
			reportReason,
			...additionalDetails && !validator.isEmpty(additionalDetails, { ignore_whitespace: true }) ? {
				additionalDetails
			} : undefined,
			urlPath: window.location.pathname
		};

		// Add reCAPTCHA token
		try { params.recaptchaToken = await executeRecaptcha('submit_content_report'); } catch (_) {}

		// Attempt report submission
		try {

			// Submit report
			await reportContent(params);

			// Update submission state
			setIsSubmitting(false);

			// Clear inputs
			setInputValues({});
			setReportReason(null);

			// Show success message
			toastSuccess(uiMode, partner, t('reportModal.confirmationMessage'));

			// Handle post report action
			hideContent();

			// Close modal
			setTimeout(() => {
				handleClose();
			}, 2000);

		} catch (_) {

			// Update submission state
			setIsSubmitting(false);

			// Show error
			toastMessage(uiMode, partner, t('reportModal.submissionError'));
		}
	};

	// Handle on input change action
	const handleOnChange = (event) => {
		const { name, value } = event.target;
		setInputValues({ ...inputValues, [name]: value });
	};

	// Handle actions on component open
	useEffect(() => {
		if (isOpen) {

			// Reset inputs
			setInputValues({});
			setReportReason(null);

		}
	}, [isOpen]);

	// Render component
	return (
		<Modal isOpen={isOpen} showOverlay handleClose={handleClose} clickOutsideClose={false}>
			<S.ModalInner>
				<S.Form>

					{/* Content */}
					<Typography tag="h3" weight="black">
						<Trans t={t} i18nKey="reportModal.title">
							How can we help?
						</Trans>
					</Typography>
					<Typography tag="p" variation="2">
						<Trans t={t} i18nKey="reportModal.description">
							Tell us why you are reporting this content. Our moderation team will review your report shortly.
						</Trans>
					</Typography>

					{/* Inputs */}
					<S.InputContainer>

						{/* Reason */}
						<RadioGroup
							value={reportReason}
							onChange={setReportReason}
							family="eventType"
							options={[
								{
									value: REPORTING_REASONS.VIOLENCE_HATE.id,
									label: t(`reportingReasons.${REPORTING_REASONS.VIOLENCE_HATE.id}.label`),
									subLabel: t(`reportingReasons.${REPORTING_REASONS.VIOLENCE_HATE.id}.sublabel`)
								},
								{
									value: REPORTING_REASONS.BULLYING.id,
									label: t(`reportingReasons.${REPORTING_REASONS.BULLYING.id}.label`),
									subLabel: t(`reportingReasons.${REPORTING_REASONS.BULLYING.id}.sublabel`)
								},
								{
									value: REPORTING_REASONS.SCAM_FRAUD.id,
									label: t(`reportingReasons.${REPORTING_REASONS.SCAM_FRAUD.id}.label`),
									subLabel: t(`reportingReasons.${REPORTING_REASONS.SCAM_FRAUD.id}.sublabel`)
								},
								{
									value: REPORTING_REASONS.MISINFORMATION.id,
									label: t(`reportingReasons.${REPORTING_REASONS.MISINFORMATION.id}.label`),
									subLabel: t(`reportingReasons.${REPORTING_REASONS.MISINFORMATION.id}.sublabel`)
								},
							]}
						/>

						{/* Additional Details */}
						<TextArea
							placeholder={t('reportModal.additionalDetailsPlaceholder')}
							name="additionalDetails"
							type="text"
							characterLimit={300}
							defaultHideCharacterLimit
							rows={4}
							value={inputValues.additionalDetails || ''}
							onChange={handleOnChange}
							style={{ margin: '25px 0px 0px' }}
						/>
					</S.InputContainer>

					{/* Actions */}
					<Button disabled={isSubmitting} isLoading={isSubmitting} onClick={submitContentReport}>
						<Trans t={t} i18nKey="actions.submit">
							Submit
						</Trans>
					</Button>
				</S.Form>
			</S.ModalInner>
		</Modal>
	);
};


/**
 * Configuration
 */

ReportModal.displayName = 'ReportModal';
ReportModal.propTypes = {
	source: PropTypes.oneOf([SOURCES.COMMENT, SOURCES.MESSAGE, SOURCES.STORY]),
	sourceId: PropTypes.string,
	isOpen: PropTypes.bool,
	handleClose: PropTypes.func,
	hideContent: PropTypes.func,
};
ReportModal.defaultProps = {
	source: null,
	sourceId: null,
	isOpen: false,
	hideContent: null,
	handleClose: null,
};
